import { AnyAction, createAction, createAsyncThunk, createSlice, ThunkDispatch } from '@reduxjs/toolkit';
import { t } from "i18next";
import axios from '../../config/axios';
import Toast from '../../shared/toast/Toast';
import { ApplicantsListRequest } from '../../utils/interfaces/applicants/ApplicantsListRequest';
import { ApplicantsListResponse } from '../../utils/interfaces/applicants/ApplicantsListResponse';
import { ResponseResult } from '../../utils/interfaces/http/ResponseResult';
import { VettingTabsEnum } from 'src/utils/enums/VettingTabsEnum';
import { ApplicantsFiltersEnumObject, VettingStoreInferface } from 'src/utils/interfaces/vetting/VettingStoreInferface';
import { AiInterviewStatusEnum, ApplicantListItem } from 'src/utils/interfaces/applicants/ApplicantListItem';
import { ApplicantsViewEnum, MassHiringStoreInferface, NewJobPostMenuStepEnum } from 'src/utils/interfaces/mass-hiring/MassHiringInterfaces';
import { NewJobPost } from 'src/utils/interfaces/job/NewJobPost';
import { JobPostHiringTypeEnum } from 'src/utils/interfaces/job/Job';
import { UpdateApplicationStagesBody } from 'src/utils/interfaces/job/Job.store';

const appKey = 'appVetting'
const apiModuleUrl = 'pa/ft/job-applications';

export const getApplicantDetails = createAsyncThunk(`${appKey}/getApplicant`, async (applicationId: string) => {
    const response = await axios.get<ResponseResult<ApplicantListItem>>(`${apiModuleUrl}/${applicationId}/details`);
    return response.data.data;
})

export const setShowNewJobPostTypeModal = createAction<boolean>(`${appKey}/setShowNewJobPostTypeModal`);
export const setNewJobPostType = createAction<JobPostHiringTypeEnum>(`${appKey}/setNewJobPostType`);
export const setNewJobPostMenuStep = createAction<NewJobPostMenuStepEnum>(`${appKey}/setNewJobPostMenuStep`);
export const saveNewJobPostData = createAction<NewJobPost>(`${appKey}/saveNewJobPostData`);
export const setIsLoadingMhMenuButton = createAction<boolean>(`${appKey}/setIsLoadingMhMenuButton`);
export const setTriggerSaveJobPost = createAction<boolean>(`${appKey}/setTriggerSaveJobPost`);
export const setTriggerSaveRecruitmentStages = createAction<boolean>(`${appKey}/setTriggerSaveRecruitmentStages`);
export const setTriggerSaveRecruitmentStagesInManageJob = createAction<boolean>(`${appKey}/setTriggerSaveRecruitmentStagesInManageJob`);
export const setTriggerSaveSaudConfiguration = createAction<boolean>(`${appKey}/setTriggerSaveSaudConfiguration`);
export const setTriggerSaveScreeningQuestions = createAction<boolean>(`${appKey}/setTriggerSaveScreeningQuestions`);
export const saveNewJobPostRecruitmentStages = createAction<UpdateApplicationStagesBody>(`${appKey}/saveNewJobPostRecruitmentStages`);
export const setIsSidebarVisible = createAction<boolean>(`${appKey}/setIsSidebarVisible`);
export const setApplicantsView = createAction<ApplicantsViewEnum>(`${appKey}/setApplicantsView`);
export const setIsRejectedTabActive = createAction<boolean>(`${appKey}/setIsRejectedTabActive`);
export const setShowSearchApplicantsInput = createAction<boolean>(`${appKey}/setShowSearchApplicantsInput`);
export const setShowFilterApplicantsModal = createAction<boolean>(`${appKey}/setShowFilterApplicantsModal`);
export const setShowRejectApplicantModal = createAction<boolean>(`${appKey}/setShowRejectApplicantModal`);
export const setRejectApplicantModalIsBulk = createAction<boolean>(`${appKey}/setRejectApplicantModalIsBulk`);
export const setShowArchiveJobModal = createAction<boolean>(`${appKey}/setShowArchiveJobModal`);
export const setShowVideoInterviewModal = createAction<boolean>(`${appKey}/setShowVideoInterviewModal`);
export const setVideoInterviewUrl = createAction<string>(`${appKey}/setVideoInterviewUrl`);
export const setShowApplyChangesToLocationsModal = createAction<boolean>(`${appKey}/setShowApplyChangesToLocationsModal`);
export const setTriggerRedirectToApplicantsList = createAction<boolean>(`${appKey}/setTriggerRedirectToApplicantsList`);

const initialState: MassHiringStoreInferface = {
    jobId: null,
    isLoadingList: true,
    showNewJobPostTypeModal: false,
    newJobPostType: null,
    newJobPostMenuStep: NewJobPostMenuStepEnum.MASTER_JOB_INFO,
    newJobPostData: null,
    newJobPostRecruitmentStages: null,
    isLoadingMhMenuButton: false,
    triggerSaveJobPost: false,
    triggerSaveRecruitmentStages: false,
    triggerSaveRecruitmentStagesInManageJob: false,
    triggerSaveSaudConfiguration: false,
    triggerSaveScreeningQuestions: false,
    isSidebarVisible: false,
    applicantsView: ApplicantsViewEnum.TABLE,
    isRejectedTabActive: false,
    showSearchApplicantsInput: false,
    showFilterApplicantsModal: false,
    showRejectApplicantModal: false,
    rejectApplicantModalIsBulk: false,
    showArchiveJobModal: false,
    showVideoInterviewModal: false,
    videoInterviewUrl: '',
    showApplyChangesToLocationsModal: false,
    triggerRedirectToApplicantsList: false
}

export const appMassHiringSlice = createSlice({
    name: appKey,
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder
            .addCase(setShowNewJobPostTypeModal, (state, action) => {
                state.showNewJobPostTypeModal = action.payload;
            })
            .addCase(setNewJobPostType, (state, action) => {
                state.newJobPostType = action.payload;
            })
            .addCase(setNewJobPostMenuStep, (state, action) => {
                state.newJobPostMenuStep = action.payload;
            })
            .addCase(saveNewJobPostData, (state, action) => {
                state.newJobPostData = action.payload;
            })
            .addCase(setIsLoadingMhMenuButton, (state, action) => {
                state.isLoadingMhMenuButton = action.payload;
            })
            .addCase(setTriggerSaveJobPost, (state, action) => {
                state.triggerSaveJobPost = action.payload;
            })
            .addCase(setTriggerSaveRecruitmentStages, (state, action) => {
                state.triggerSaveRecruitmentStages = action.payload;
            })
            .addCase(setTriggerSaveRecruitmentStagesInManageJob, (state, action) => {
                state.triggerSaveRecruitmentStagesInManageJob = action.payload;
            })
            .addCase(setTriggerSaveSaudConfiguration, (state, action) => {
                state.triggerSaveSaudConfiguration = action.payload;
            })
            .addCase(setTriggerSaveScreeningQuestions, (state, action) => {
                state.triggerSaveScreeningQuestions = action.payload;
            })
            .addCase(saveNewJobPostRecruitmentStages, (state, action) => {
                state.newJobPostRecruitmentStages = action.payload;
            })
            .addCase(setIsSidebarVisible, (state, action) => {
                state.isSidebarVisible = action.payload;
            })
            .addCase(setApplicantsView, (state, action) => {
                state.applicantsView = action.payload;
            })
            .addCase(setIsRejectedTabActive, (state, action) => {
                state.isRejectedTabActive = action.payload;
            })
            .addCase(setShowSearchApplicantsInput, (state, action) => {
                state.showSearchApplicantsInput = action.payload;
            })
            .addCase(setShowFilterApplicantsModal, (state, action) => {
                state.showFilterApplicantsModal = action.payload;
            })
            .addCase(setShowRejectApplicantModal, (state, action) => {
                state.showRejectApplicantModal = action.payload;
            })
            .addCase(setRejectApplicantModalIsBulk, (state, action) => {
                state.rejectApplicantModalIsBulk = action.payload;
            })
            .addCase(setShowArchiveJobModal, (state, action) => {
                state.showArchiveJobModal = action.payload;
            })
            .addCase(setShowVideoInterviewModal, (state, action) => {
                state.showVideoInterviewModal = action.payload;
            })
            .addCase(setVideoInterviewUrl, (state, action) => {
                state.videoInterviewUrl = action.payload;
            })
            .addCase(setShowApplyChangesToLocationsModal, (state, action) => {
                state.showApplyChangesToLocationsModal = action.payload;
            })
            .addCase(setTriggerRedirectToApplicantsList, (state, action) => {
                state.triggerRedirectToApplicantsList = action.payload;
            })
    }
})

export default appMassHiringSlice.reducer
